/**
 * Map controller (03 §5): wheel zoom anchored at the cursor, drag pan with inertia, double-click
 * zoom, pan clamp, click-to-follow, idle crisp snap, and — for touch — multi-touch pan/pinch and
 * double-tap (plan T1/T2). Pure logic (no DOM, no Babylon): the shell maps pointer events to
 * **stage px** and calls the methods below; `update(dtWall)` runs once per frame. Pointers are
 * tracked by id: one tracked pointer pans exactly as the mouse does, two pinch-zoom around their
 * midpoint (the same `zoomAt` / clamp / follow rules as the wheel). On a coarse pointer the
 * single-tap action defers past the double-tap window on the controller clock (review TF3), and
 * every gesture zoom reports through `onZoom` (review TF4).
 */
import type { Rect } from '../api.ts';
import { idleCrispTarget, w2s } from './projection.ts';
import type { MapView } from './view.ts';

/** Pointer input, already in stage px (the shell maps CSS px → device px → stage px, 09 §7). */
export type ControllerInput =
  | { type: 'wheel'; x: number; y: number; deltaY: number; deltaMode?: 0 | 1 | 2 }
  | { type: 'down'; x: number; y: number; button: number; pointerId?: number; touch?: boolean }
  | { type: 'move'; x: number; y: number; pointerId?: number }
  | { type: 'up'; x: number; y: number; button: number; pointerId?: number }
  | { type: 'cancel'; pointerId?: number }
  | { type: 'dblclick'; x: number; y: number }
  | { type: 'escape' };

export const WHEEL_ZOOM = 1.15;
export const DBLCLICK_ZOOM = 2;
/** Inertia decay per 60 Hz frame. */
export const INERTIA_DECAY = 0.85;
export const IDLE_MS = 150;
/** Click-to-follow pick radius (stage px); 22 for a coarse pointer (T1). */
export const PICK_RADIUS_PX = 12;
export const TOUCH_PICK_RADIUS_PX = 22;
/** A press that moves less than this (stage px) is a click, not a drag; 10 for a coarse pointer. */
export const CLICK_SLOP_PX = 4;
export const TOUCH_CLICK_SLOP_PX = 10;
/** Two taps within this time (controller clock) and distance (stage px) zoom ×2, like dblclick (T1). */
export const DBL_TAP_MS = 300;
export const DBL_TAP_RADIUS_PX = 24;
/** Duration of the animated idle crisp snap. */
export const SNAP_ANIM_S = 0.12;

export interface ControllerOptions {
  /** Notified on a click on the map: the picked car (nearest within the pick radius) or null;
   *  `touch` says the click came from a coarse pointer (T2: an empty-map tap then toggles the chrome). */
  onPick?: (car: number | null, touch: boolean) => void;
  /** Follow the picked car automatically (default true). */
  clickToFollow?: boolean;
  /** Notified on every gesture zoom that changes the scale (wheel, double-click/double-tap, pinch,
   *  keyboard zoomBy). The shell marks the view user-moved for the deep link there: a touch zoom
   *  fires no wheel or dblclick the shell could hook itself (review TF4). */
  onZoom?: () => void;
}

function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
}

export class MapController {
  inputRect: Rect;
  exclusions: Rect[] = [];
  enabled = true;
  private opts: ControllerOptions;

  // drag state
  private dragging = false;
  private dragMoved = false;
  private downX = 0;
  private downY = 0;
  private lastX = 0;
  private lastY = 0;
  private lastMoveT = 0;
  private vx = 0; // stage px / s (drag velocity estimate while dragging, inertia after release)
  private vy = 0;
  private frameDx = 0; // drag displacement since the last update()
  private frameDy = 0;
  private now = 0; // controller clock (s), advanced by update()
  // T1 multi-touch: up to two tracked pointers (id −1 = free slot); one pans, two pinch
  private readonly ptrId: [number, number] = [-1, -1];
  private readonly ptrX: [number, number] = [0, 0];
  private readonly ptrY: [number, number] = [0, 0];
  private pinching = false;
  // the active pointer is coarse: wider pick radius and click slop (T1)
  private coarse = false;
  private pickRadius = PICK_RADIUS_PX;
  private slopPx = CLICK_SLOP_PX;
  // double-tap (T1): the last tap, and the double-tap this controller zoomed itself (the browser's
  // synthetic dblclick for the same gesture is then swallowed, so the zoom applies exactly once)
  private tapT = -Infinity;
  private tapX = 0;
  private tapY = 0;
  private dblTapT = -Infinity;
  private dblTapX = 0;
  private dblTapY = 0;
  // TF3: on a coarse pointer the single-tap action (pick/follow, the shell's chrome toggle) waits
  // out the double-tap window on the controller clock, so the first tap of a double-tap only zooms
  private pendingTap: { x: number; y: number; t: number; touch: boolean } | null = null;
  // idle / snap
  private idleT = 0;
  private gestureActive = false;
  private gestureStartMpp = 0;
  private anchorX = 0;
  private anchorY = 0;
  private gestureZoomed = false;
  private readonly snap = { active: false, t: 0, x0: 0, y0: 0, l0: 0, x1: 0, y1: 0, l1: 0, m1: 0 };
  private readonly end = [0, 0, 0];
  private readonly tmp: number[] = [0, 0];
  private readonly tmp2: number[] = [0, 0];

  constructor(
    readonly view: MapView,
    inputRect?: Rect,
    opts: ControllerOptions = {},
  ) {
    this.inputRect = inputRect ? { ...inputRect } : { ...view.rect };
    this.opts = opts;
  }

  setInput(inputRect: Rect, exclusions: Rect[] = []): void {
    this.inputRect = { ...inputRect };
    this.exclusions = exclusions.map((r) => ({ ...r }));
  }

  /** True if (x, y) stage px is inside the input rect and outside every exclusion. */
  accepts(x: number, y: number): boolean {
    if (!this.enabled || !inRect(this.inputRect, x, y)) return false;
    for (const r of this.exclusions) if (inRect(r, x, y)) return false;
    return true;
  }

  get isDragging(): boolean {
    return this.dragging && this.dragMoved;
  }

  /** Dispatch one input event. Returns true if the controller consumed it. */
  handle(ev: ControllerInput): boolean {
    switch (ev.type) {
      case 'wheel':
        return this.wheel(ev.x, ev.y, ev.deltaY, ev.deltaMode ?? 0);
      case 'down':
        return this.pointerDown(ev.x, ev.y, ev.button, ev.pointerId ?? 0, ev.touch ?? false);
      case 'move':
        return this.pointerMove(ev.x, ev.y, ev.pointerId ?? 0);
      case 'up':
        return this.pointerUp(ev.x, ev.y, ev.button, ev.pointerId ?? 0);
      case 'cancel':
        return this.pointerCancel(ev.pointerId ?? 0);
      case 'dblclick':
        return this.dblclick(ev.x, ev.y);
      case 'escape':
        return this.escape();
    }
  }

  /** Wheel: ×1.15 per notch (100 px of pixel delta = 1 notch), anchored at the cursor. */
  wheel(x: number, y: number, deltaY: number, deltaMode: 0 | 1 | 2 = 0): boolean {
    if (!this.accepts(x, y) || deltaY === 0) return false;
    let notches = deltaMode === 1 ? deltaY / 3 : deltaMode === 2 ? deltaY : deltaY / 100;
    notches = Math.max(-3, Math.min(3, notches));
    this.zoomGesture(x, y, Math.pow(WHEEL_ZOOM, -notches));
    return true;
  }

  dblclick(x: number, y: number): boolean {
    if (!this.accepts(x, y)) return false;
    // a touch double-tap already zoomed this gesture (T1): swallow the browser's dblclick so the
    // zoom applies exactly once, but report it consumed (the view did move)
    if (this.now - this.dblTapT <= 0.5 && Math.hypot(x - this.dblTapX, y - this.dblTapY) <= DBL_TAP_RADIUS_PX) {
      this.dblTapT = -Infinity;
      return true;
    }
    this.zoomGesture(x, y, DBLCLICK_ZOOM);
    return true;
  }

  /** Programmatic zoom (keyboard +/-) at the map-rect centre or the followed car. */
  zoomBy(factor: number): void {
    const r = this.view.rect;
    this.zoomGesture(r.x + r.w / 2, r.y + r.h / 2, factor);
  }

  private zoomGesture(x: number, y: number, factor: number): void {
    const v = this.view;
    this.beginGesture();
    v.cancelFly();
    this.vx = this.vy = 0;
    let ax = x;
    let ay = y;
    if (v.following !== null) {
      // keep the followed car where it is (follow re-centres anyway)
      ax = v.cpx;
      ay = v.cpy;
    }
    v.zoomAt(ax, ay, factor);
    this.gestureZoomed = true;
    if (v.following === null) v.clampToExtent(); // never fight follow
    this.anchorX = ax;
    this.anchorY = ay;
    this.opts.onZoom?.();
  }

  pointerDown(x: number, y: number, button: number, pointerId = 0, touch = false): boolean {
    if ((button !== 0 && button !== 1) || !this.accepts(x, y)) return false;
    const slot = this.ptrId[0] === -1 ? 0 : this.ptrId[1] === -1 && this.ptrId[0] !== pointerId ? 1 : -1;
    if (slot === -1) return false; // a third pointer (or a repeat) joins no gesture
    this.ptrId[slot] = pointerId;
    this.ptrX[slot] = x;
    this.ptrY[slot] = y;
    if (slot === 1) {
      // a second finger starts a pinch: never a click, even if it lifts at once — and it cancels a
      // still-deferred single tap (TF3), like the native gesture would
      this.pinching = true;
      this.dragMoved = true;
      this.pendingTap = null;
      this.vx = this.vy = 0;
      this.frameDx = this.frameDy = 0;
      return true;
    }
    this.coarse = touch;
    this.pickRadius = touch ? TOUCH_PICK_RADIUS_PX : PICK_RADIUS_PX;
    this.slopPx = touch ? TOUCH_CLICK_SLOP_PX : CLICK_SLOP_PX;
    this.dragging = true;
    this.dragMoved = false;
    this.downX = this.lastX = x;
    this.downY = this.lastY = y;
    this.lastMoveT = this.now;
    this.vx = this.vy = 0;
    this.frameDx = this.frameDy = 0;
    this.snap.active = false;
    return true;
  }

  pointerMove(x: number, y: number, pointerId = 0): boolean {
    if (this.pinching) {
      const i = this.ptrId[0] === pointerId ? 0 : this.ptrId[1] === pointerId ? 1 : -1;
      if (i === -1) return false;
      this.pinchMove(i, x, y);
      return true;
    }
    if (!this.dragging || this.ptrId[0] !== pointerId) return false;
    if (!this.dragMoved) {
      if (Math.hypot(x - this.downX, y - this.downY) < this.slopPx) return true;
      this.dragMoved = true;
      this.pendingTap = null; // a drag cancels the deferred single-tap action (TF3)
      this.beginGesture();
      this.view.cancelFly();
      this.view.follow(null); // drag cancels follow
      // apply the slop distance too, so the picture stays glued to the pointer
      this.lastX = this.downX;
      this.lastY = this.downY;
    }
    const dx = x - this.lastX;
    const dy = y - this.lastY;
    this.frameDx += dx;
    this.frameDy += dy;
    this.lastMoveT = this.now;
    this.lastX = x;
    this.lastY = y;
    this.view.panByClamped(dx, dy);
    this.anchorX = x;
    this.anchorY = y;
    this.idleT = 0;
    return true;
  }

  /**
   * Two-pointer update (T1): zoom by the pointer-distance ratio about the OLD midpoint (the same
   * `zoomAt` / clamp / follow rules as the wheel), then pan by the midpoint displacement, so the
   * world under the old midpoint follows the fingers. While following, only the zoom applies
   * (follow re-centres anyway). Rotation of the pair is not tracked.
   */
  private pinchMove(i: number, x: number, y: number): void {
    const j = 1 - i;
    const sx = this.ptrX[i] ?? 0;
    const sy = this.ptrY[i] ?? 0;
    const ox = this.ptrX[j] ?? 0;
    const oy = this.ptrY[j] ?? 0;
    const m0x = (sx + ox) / 2;
    const m0y = (sy + oy) / 2;
    const d0 = Math.hypot(sx - ox, sy - oy);
    this.ptrX[i] = x;
    this.ptrY[i] = y;
    const m1x = (x + ox) / 2;
    const m1y = (y + oy) / 2;
    const d1 = Math.hypot(x - ox, y - oy);
    const v = this.view;
    this.beginGesture();
    v.cancelFly();
    this.vx = this.vy = 0;
    if (d0 > 1 && d1 > 1 && d1 !== d0) this.zoomGesture(m0x, m0y, d1 / d0);
    if (v.following === null) {
      v.panByClamped(m1x - m0x, m1y - m0y);
      this.anchorX = m1x;
      this.anchorY = m1y;
    }
    this.idleT = 0;
  }

  pointerUp(x: number, y: number, button: number, pointerId = 0): boolean {
    const slot = this.ptrId[0] === pointerId ? 0 : this.ptrId[1] === pointerId ? 1 : -1;
    if (slot === -1 || (button !== 0 && button !== 1)) return false;
    this.ptrId[slot] = -1;
    if (this.pinching) {
      if (this.ptrId[0] !== -1 || this.ptrId[1] !== -1) {
        this.liftOnePointer();
        return true;
      }
      this.pinching = false;
    }
    if (!this.dragging) return false;
    this.dragging = false;
    if (!this.dragMoved) {
      this.vx = this.vy = 0;
      if (button === 0) this.click(x, y);
      return true;
    }
    // released after a pause: no fling
    if (this.now - this.lastMoveT > 0.08) this.vx = this.vy = 0;
    this.frameDx = this.frameDy = 0;
    return true;
  }

  /** pointercancel: release the pointer with no click and no fling (T1). */
  pointerCancel(pointerId = 0): boolean {
    const slot = this.ptrId[0] === pointerId ? 0 : this.ptrId[1] === pointerId ? 1 : -1;
    if (slot === -1) return false;
    this.ptrId[slot] = -1;
    if (this.pinching) {
      if (this.ptrId[0] !== -1 || this.ptrId[1] !== -1) {
        this.liftOnePointer();
        return true;
      }
      this.pinching = false;
    }
    this.dragging = false;
    this.vx = this.vy = 0;
    this.frameDx = this.frameDy = 0;
    return true;
  }

  /** One of two pointers lifted/cancelled (T1): the remaining finger continues as a one-finger
   *  pan from its own position, with no jump and no click on its eventual release. */
  private liftOnePointer(): void {
    this.pinching = false;
    // compact the survivor into slot 0, the single-pointer slot
    if (this.ptrId[0] === -1) {
      this.ptrId[0] = this.ptrId[1];
      this.ptrX[0] = this.ptrX[1];
      this.ptrY[0] = this.ptrY[1];
      this.ptrId[1] = -1;
    }
    this.downX = this.lastX = this.ptrX[0];
    this.downY = this.lastY = this.ptrY[0];
    this.lastMoveT = this.now;
    this.vx = this.vy = 0;
    this.frameDx = this.frameDy = 0;
    this.dragging = true;
  }

  escape(): boolean {
    if (this.view.following === null) return false;
    this.view.follow(null);
    return true;
  }

  private click(x: number, y: number): void {
    if (this.coarse) {
      // double-tap (T1): two taps within 300 ms and 24 stage px zoom ×2, like dblclick; the
      // controller does not wait for the browser to synthesize one (and swallows it if it does)
      if (
        this.now - this.dblTapT > 0.5 &&
        this.now - this.tapT <= DBL_TAP_MS / 1000 &&
        Math.hypot(x - this.tapX, y - this.tapY) <= DBL_TAP_RADIUS_PX
      ) {
        this.tapT = -Infinity;
        this.dblTapT = this.now;
        this.dblTapX = x;
        this.dblTapY = y;
        this.pendingTap = null; // the zoom replaces both taps' single-tap actions
        this.zoomGesture(x, y, DBLCLICK_ZOOM);
        return;
      }
      this.tapT = this.now;
      this.tapX = x;
      this.tapY = y;
      // TF3: this tap may still be the first of a double-tap: hold its action for the window. A
      // tap that completes no double-tap (outside the time or the radius) releases the older
      // pending action first; the window closing without a second tap fires it in update().
      this.firePendingTap();
      this.pendingTap = { x, y, t: this.now, touch: true };
      return;
    }
    this.pick(x, y, this.coarse, this.pickRadius);
  }

  /** Fire the deferred single-tap action (TF3): the pick the shell acts on, at the tap's position
   *  and with the tap's pointer kind and pick radius (a later pointer may have changed both). */
  private firePendingTap(): void {
    const t = this.pendingTap;
    if (!t) return;
    this.pendingTap = null;
    this.pick(t.x, t.y, t.touch, t.touch ? TOUCH_PICK_RADIUS_PX : PICK_RADIUS_PX);
  }

  private pick(x: number, y: number, touch: boolean, radius: number): void {
    const car = this.pickCar(x, y, radius);
    this.opts.onPick?.(car, touch);
    if (car !== null && (this.opts.clickToFollow ?? true)) this.view.follow(car);
  }

  /** Nearest car within the pick radius (12 stage px; 22 while a coarse pointer is active, T1), or null. */
  pickCar(x: number, y: number, radius: number = this.pickRadius): number | null {
    const src = this.view.carSource;
    if (!src) return null;
    let best: number | null = null;
    let bestD = radius * radius;
    for (let i = 0; i < src.count; i++) {
      if (!src.position(i, this.tmp)) continue;
      w2s(this.view, this.tmp[0] ?? 0, this.tmp[1] ?? 0, this.tmp2);
      const dx = (this.tmp2[0] ?? 0) - x;
      const dy = (this.tmp2[1] ?? 0) - y;
      const d = dx * dx + dy * dy;
      if (d <= bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  private beginGesture(): void {
    if (!this.gestureActive) {
      this.gestureActive = true;
      this.gestureStartMpp = this.view.mpp;
    }
    this.snap.active = false;
    this.idleT = 0;
  }

  /**
   * Per-frame update (wall-clock dt, UI time): inertia, the idle timer and the animated crisp
   * snap, then the view's own fly-to/follow update. Allocation-free.
   */
  update(dtWall: number): void {
    this.now += dtWall;
    // TF3: the double-tap window closed with no second tap: the deferred single-tap action fires
    if (this.pendingTap && this.now - this.pendingTap.t > DBL_TAP_MS / 1000) this.firePendingTap();
    const v = this.view;
    if (this.dragging && this.dragMoved && dtWall > 0) {
      // smoothed drag velocity (stage px / s) for the fling on release
      this.vx = 0.5 * this.vx + 0.5 * (this.frameDx / dtWall);
      this.vy = 0.5 * this.vy + 0.5 * (this.frameDy / dtWall);
      this.frameDx = this.frameDy = 0;
    }
    // inertia (only after release)
    if (!this.dragging && (this.vx !== 0 || this.vy !== 0)) {
      const decay = Math.pow(INERTIA_DECAY, dtWall * 60);
      const clipped = v.panByClamped(this.vx * dtWall, this.vy * dtWall);
      if (clipped & 1) this.vx = 0; // zero only the clipped component
      if (clipped & 2) this.vy = 0;
      this.vx *= decay;
      this.vy *= decay;
      if (Math.hypot(this.vx, this.vy) < 5) this.vx = this.vy = 0;
      this.idleT = 0;
    }
    v.update(dtWall);
    if (this.snap.active) {
      // interpolate centre and log-mpp together towards the (already clamped) end state
      const s = this.snap;
      s.t += dtWall;
      const a = Math.min(1, s.t / SNAP_ANIM_S);
      const e = a * a * (3 - 2 * a);
      if (a >= 1) v.setViewRaw(s.x1, s.y1, s.m1);
      else v.setViewRaw(s.x0 + (s.x1 - s.x0) * e, s.y0 + (s.y1 - s.y0) * e, Math.exp(s.l0 + (s.l1 - s.l0) * e));
      if (a >= 1) {
        s.active = false;
        this.endGesture();
      }
      return;
    }
    if (this.gestureActive && !this.dragging && this.vx === 0 && this.vy === 0) {
      this.idleT += dtWall;
      if (this.idleT * 1000 >= IDLE_MS) this.startSnap();
    }
  }

  /**
   * Begin the idle snap. Zoom gestures snap mpp to a crisp level (anchored at the last cursor);
   * pan-only gestures snap only the translation to whole stage px. The end state is computed
   * clamped up front, and the animation interpolates towards it (no jump at the end).
   */
  private startSnap(): void {
    const v = this.view;
    const target = this.gestureZoomed ? idleCrispTarget(v.mpp, this.gestureStartMpp, v.crispLevels()) : v.mpp;
    v.snapEndState(this.anchorX, this.anchorY, target, this.end);
    const s = this.snap;
    s.x0 = v.cx;
    s.y0 = v.cy;
    s.x1 = this.end[0] ?? v.cx;
    s.y1 = this.end[1] ?? v.cy;
    s.m1 = this.end[2] ?? v.mpp;
    s.l0 = Math.log(v.mpp);
    s.l1 = Math.log(s.m1);
    const dPx = Math.hypot(s.x1 - s.x0, s.y1 - s.y0) / v.mpp;
    if (Math.abs(s.l1 - s.l0) < 1e-9 && dPx < 1) {
      // sub-pixel translation snap: apply directly
      if (s.x1 !== v.cx || s.y1 !== v.cy || s.m1 !== v.mpp) v.setViewRaw(s.x1, s.y1, s.m1);
      this.endGesture();
      return;
    }
    s.active = true;
    s.t = 0;
  }

  private endGesture(): void {
    this.gestureActive = false;
    this.gestureZoomed = false;
    this.view.notifyIdle();
  }
}
